import React from 'react';
import { Avatar, Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import colors from '../../../assets/theme/colors';
import { getChildPhotoSource } from '../../../utils/childPhoto';
import { getChildProfileSetupStatus } from '../../../utils/profileCompletion';

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

export const ProfileSetupAvatar = ({
  child,
  setupStatus,
  size = 44,
  ringWidth = 3,
  mode = 'edit',
  accentColor,
  percentBadgeSx = {},
  showPercentBadge = false,
  showAttentionDot = false,
  avatarSx = {},
  sx = {},
}) => {
  const status = setupStatus || getChildProfileSetupStatus(child || {});
  const percent = clampPercent(status.completionPercent);
  const photoSrc = getChildPhotoSource(child);
  const initial = (child?.name?.trim()?.[0] || 'P').toUpperCase();
  const isViewOnly = mode === 'view';
  const isIncomplete = !status.isComplete && percent < 100;
  const hasLargeBadge = size >= 96;
  // Identity color only - this is a plain, complete ring around the avatar
  // (not a completion indicator). It always reflects the child's accent
  // color from childAccentTheme.js, regardless of profile-setup progress.
  const accent = accentColor || colors.brand.deep;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        ...sx,
      }}
    >
      <Avatar
        src={photoSrc || undefined}
        alt={child?.name || 'Profile avatar'}
        sx={{
          width: size,
          height: size,
          boxSizing: 'border-box',
          bgcolor: photoSrc ? '#EAF2EE' : isViewOnly ? colors.brand.tint : '#D9E7D7',
          color: isViewOnly ? colors.brand.deep : '#1F2937',
          fontWeight: 900,
          fontSize: size <= 28 ? '0.72rem' : size <= 56 ? '0.94rem' : `${Math.round(size * 0.42)}px`,
          border: `${ringWidth}px solid ${accent}`,
          boxShadow: `0 8px 18px ${alpha(colors.brand.tint, 0.16)}`,
          ...avatarSx,
        }}
      >
        {!photoSrc ? initial : null}
      </Avatar>
      {isViewOnly ? (
        <Box
          sx={{
            position: 'absolute',
            right: -2,
            bottom: -2,
            width: Math.max(18, Math.round(size * 0.34)),
            height: Math.max(18, Math.round(size * 0.34)),
            borderRadius: '50%',
            bgcolor: colors.brand.deep,
            color: colors.semantic.profileFlow.cardBg,
            border: `2px solid ${colors.semantic.profileFlow.cardBg}`,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: Math.max(12, Math.round(size * 0.18)) }} />
        </Box>
      ) : null}
      {showPercentBadge && !isViewOnly ? (
        <Box
          sx={{
            position: 'absolute',
            right: -8,
            bottom: -3,
            minWidth: hasLargeBadge ? 48 : 34,
            px: hasLargeBadge ? 0.9 : 0.65,
            py: hasLargeBadge ? 0.28 : 0.18,
            borderRadius: 999,
            bgcolor: colors.semantic.profileFlow.progressFilled,
            color: colors.semantic.profileFlow.cardBg,
            border: `2px solid ${colors.semantic.profileFlow.cardBg}`,
            fontSize: hasLargeBadge ? '1rem' : size <= 32 ? '0.62rem' : '0.72rem',
            fontWeight: 900,
            lineHeight: 1.1,
            textAlign: 'center',
            ...percentBadgeSx,
          }}
        >
          {percent}%
        </Box>
      ) : null}
      {showAttentionDot && !isViewOnly && isIncomplete ? (
        <Box
          sx={{
            position: 'absolute',
            right: -4,
            bottom: showPercentBadge ? Math.max(12, Math.round(size * 0.46)) : Math.max(2, Math.round(size * 0.14)),
            width: Math.max(12, Math.round(size * 0.22)),
            height: Math.max(12, Math.round(size * 0.22)),
            borderRadius: '50%',
            bgcolor: colors.mood.happy,
            border: `2px solid ${colors.semantic.profileFlow.cardBg}`,
            boxShadow: `0 0 0 0 ${alpha(colors.mood.happy, 0.48)}`,
            animation: 'profileAttentionPulse 1.8s ease-out infinite',
            '@keyframes profileAttentionPulse': {
              '0%': { boxShadow: `0 0 0 0 ${alpha(colors.mood.happy, 0.48)}` },
              '70%': { boxShadow: `0 0 0 7px ${alpha(colors.mood.happy, 0)}` },
              '100%': { boxShadow: `0 0 0 0 ${alpha(colors.mood.happy, 0)}` },
            },
          }}
        />
      ) : null}
    </Box>
  );
};

export const ProfileSetupIdentity = ({
  child,
  setupStatus,
  title,
  subtitle = '',
  mode = 'edit',
  avatarSize = 44,
  ringWidth = 3,
  accentColor,
  percentBadgeSx = {},
  showPercentBadge = false,
  showAttentionDot = false,
  showCompletionLabel = false,
  showCompletionDot = true,
  avatarSx = {},
  avatarContainerSx = {},
  sx = {},
  contentSx = {},
  titleSx = {},
  subtitleSx = {},
  completionRowSx = {},
  completionLabelSx = {},
  completionDotSx = {},
}) => {
  const status = setupStatus || getChildProfileSetupStatus(child || {});
  const displayTitle = title || child?.name || 'Profile';
  const completionLabel = status.completionLabel || status.completionSummary?.label || `${clampPercent(status.completionPercent)}% complete`;
  const hasRemaining = status.completionSummary ? status.completionSummary.remaining > 0 : !status.isComplete;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        ...sx,
      }}
    >
      <ProfileSetupAvatar
        child={child}
        setupStatus={status}
        size={avatarSize}
        ringWidth={ringWidth}
        mode={mode}
        accentColor={accentColor}
        percentBadgeSx={percentBadgeSx}
        showPercentBadge={showPercentBadge}
        showAttentionDot={showAttentionDot}
        avatarSx={avatarSx}
        sx={avatarContainerSx}
      />

      <Box sx={{ minWidth: 0, ...contentSx }}>
        <Typography
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            fontSize: '1rem',
            lineHeight: 1.05,
            fontWeight: 800,
            color: colors.semantic.ink,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            ...titleSx,
          }}
        >
          {displayTitle}
        </Typography>
        {subtitle ? (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: '0.84rem',
              color: colors.semantic.ink2,
              lineHeight: 1.2,
              ...subtitleSx,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
        {showCompletionLabel ? (
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              ...completionRowSx,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                px: 0.85,
                py: 0.34,
                borderRadius: 999,
                bgcolor: colors.semantic.profileFlow.completionBg,
                color: colors.semantic.profileFlow.completionText,
                fontWeight: 700,
                fontSize: '0.72rem',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                ...completionLabelSx,
              }}
            >
              {completionLabel}
            </Box>
            {showCompletionDot && hasRemaining ? (
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  border: `2px solid ${colors.mood.happy}`,
                  flexShrink: 0,
                  ...completionDotSx,
                }}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export const ProfileSetupPill = ({
  setupStatus,
  onClick,
  prefix = 'Finish',
  compact = false,
  sx = {},
}) => {
  if (!setupStatus?.shouldShowSingleItemPill || !setupStatus.nextItem) {
    return null;
  }

  const label = `${prefix}: ${setupStatus.nextItem.pillText || setupStatus.nextItem.label}`;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={!onClick}
      aria-label={label}
      sx={{
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.35,
        minHeight: compact ? 30 : 34,
        maxWidth: '100%',
        px: compact ? 0.85 : 1,
        py: compact ? 0.35 : 0.45,
        borderRadius: '999px',
        bgcolor: alpha(colors.semantic.profileFlow.completionBg, 0.95),
        border: `1px solid ${alpha(colors.semantic.profileFlow.progressFilled, 0.28)}`,
        color: colors.brand.deep,
        '&:hover': {
          bgcolor: colors.semantic.profileFlow.completionBg,
        },
        '&.Mui-disabled': {
          opacity: 1,
          color: colors.brand.deep,
        },
        ...sx,
      }}
    >
      <Typography
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: compact ? '0.7rem' : '0.76rem',
          fontWeight: 850,
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
      <ChevronRightRoundedIcon sx={{ flexShrink: 0, fontSize: compact ? 16 : 18, color: 'inherit' }} />
    </ButtonBase>
  );
};
